import axios from 'axios';
import API_CONFIG from '../config/api';

/**
 * Content Service - Real API Integration
 * Handles all content management operations with the backend API
 */

// Content type constants
export const CONTENT_TYPES = {
  ARTICLE: 'article',
  IMAGE: 'image',
  VIDEO: 'video',
  ALBUM: 'album',
  AUDIO: 'audio'
};

// Content status constants
export const CONTENT_STATUS = {
  DRAFT: false,
  PUBLISHED: true
};

/**
 * Reorder multiple content items
 * @param postId
 * @param {Array} contentItems - Array of content items with new order
 * @returns {Promise<Object>} Success response
 */
export const reorderContent = async (postId, contentItems) => {
    try {
        // Transform content items to the format expected by API
        const reorderData = {
            content_items: contentItems.map(item => ({
                id: item.id,
                order_index: item.order_index
            }))
        };
        const response = await axios.patch(`${API_CONFIG.API_URL}/post/${postId}/reorder`, reorderData);
        return response.data;
    } catch (error) {
        console.error('Error reordering content:', error);
        throw new Error(error.response?.data?.detail || 'Failed to reorder content');
    }
};

/**
 * Upload an image file
 * @param {File} file - Image file to upload
 * @returns {Promise<Object>} Upload response with file URL
 */
export const uploadImage = async (file) => {
  try {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload JPG, PNG, GIF, or WebP images.');
    }
    
    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      throw new Error('File size too large. Please upload images smaller than 5MB.');
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(`${API_CONFIG.API_URL}/pages/upload/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error uploading image:', error);
    if (error.message.includes('Invalid file type') || error.message.includes('File size too large')) {
      throw error;
    }
    throw new Error(error.response?.data?.detail || 'Failed to upload image');
  }
};

// Export all functions as default object for easier importing
const contentService = {
  uploadImage,
  reorderContent,
  CONTENT_TYPES,
  CONTENT_STATUS
};

export default contentService;